import { useQuery } from '@apollo/client';
import { Button, Typography } from '@mui/material';
import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ManageDataGrid } from '~/components/data-grid';
import { Dialog, DialogTitle } from '~/components/dialogs/components';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { DialogActions, DialogContent } from '~/components/dialogs/lib/styles';
import { useAppContext } from '~/contexts';
import { DesignDynamicFiltersDocument } from '../DesignList.generated';
import { useTagUtils } from '../lib';

export interface TagsDialogProps {
  onClose: () => void;
  open: boolean;
}

export const TagsDialog = ({ onClose, open }: TagsDialogProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const { data } = useQuery(DesignDynamicFiltersDocument, {
    variables: {
      currentNetworkId: currentNetwork.id,
    },
    fetchPolicy: 'cache-first',
  });

  const {
    cellModesModel,
    columns,
    confirmDeleteProps,
    handleCellEditStart,
    handleCellEditStop,
    handleCellFocus,
    handleCellKeyDown,
    processRowUpdate,
    rows,
    selectedCellParams,
    setCellModesModel,
  } = useTagUtils(data?.network?.designTags || []);

  const selectedRow = useMemo(
    () => rows.find((x) => x.id === selectedCellParams?.id),
    [rows, selectedCellParams],
  );

  const designLabel =
    selectedRow?.uses === 1
      ? t('pages.studio.dialogs.manage_tags.design')
      : t('pages.studio.dialogs.manage_tags.designs');

  return (
    <Dialog onClose={onClose} fullWidth maxWidth="sm" open={open}>
      <DialogTitle onClose={onClose}>{t('common.tags.manage_tags')}</DialogTitle>
      <DialogContent>
        {rows.length > 0 ? (
          <ManageDataGrid
            cellModesModel={cellModesModel}
            columns={columns}
            disableColumnMenu
            disableRowSelectionOnClick
            localeText={{
              MuiTablePagination: {
                labelRowsPerPage: t('common.pagination.per_page'),
              },
              footerTotalRows: t('common.pagination.total_rows'),
            }}
            onCellEditStart={handleCellEditStart}
            onCellEditStop={handleCellEditStop}
            onCellKeyDown={handleCellKeyDown}
            onCellModesModelChange={setCellModesModel}
            processRowUpdate={processRowUpdate}
            rows={rows}
            slots={{
              columnResizeIcon: () => null,
            }}
            slotProps={{
              cell: {
                onFocus: handleCellFocus,
              },
            }}
          />
        ) : (
          <Typography variant="subtitle2" sx={{ paddingY: 2 }}>
            {t('common.tags.no_tags')}
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          {t('common.dialogs.close')}
        </Button>
      </DialogActions>

      <ConfirmDialog
        {...confirmDeleteProps}
        cancel={t('common.dialogs.cancel')}
        confirm={t('pages.studio.dialogs.manage_tags.delete_tag_confirm')}
        deleteConfirm
        prompt={
          <Trans
            i18nKey="pages.studio.dialogs.manage_tags.delete_tag_prompt"
            values={{ name: selectedRow?.name, uses: selectedRow?.uses, design: designLabel }}
            components={{ 0: <b />, 1: <strong />, 2: <strong /> }}
          />
        }
        title={t('common.tags.delete_tag')}
      />
    </Dialog>
  );
};
